import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { User, LoginRequest, TwoFactorVerifyRequest } from '@/types';
import authService from '@/services/auth';
import sessionsService from '@/services/sessionsService';
import multiTenantPermissionService from '@/services/multiTenantPermissionService';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginRequest) => Promise<void>;
  twoFactorAuth: (code: TwoFactorVerifyRequest) => Promise<void>;
  changePassword: (data: { current_password: string; new_password: string; confirm_password: string }) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const hasToken = authService.isAuthenticated();
        
        if (hasToken) {
          await refreshUser();
        } else {
        }
      } catch (error) {
        console.error('❌ Échec de l\'initialisation de l\'authentification:', error);
        // En cas d'erreur, nettoyer les tokens
        authService.clearTokens();
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    // Délai pour éviter les conflits avec les redirections
    const timeoutId = setTimeout(initializeAuth, 50);
    
    return () => clearTimeout(timeoutId);
  }, []);

  const login = async (credentials: LoginRequest) => {
    try {
      setIsLoading(true);
      const response = await authService.login(credentials);
      if (response.success && response.data?.user) {
        setUser(response.data.user);
        
        // Initialize permissions after successful login
        try {
          await multiTenantPermissionService.initializeUserPermissions();
        } catch (permError) {
          console.warn('⚠️ Erreur lors de l\'initialisation des permissions:', permError);
        }
      } else {
        throw new Error(response.message || 'Login failed');
      }
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const twoFactorAuth = async (code: TwoFactorVerifyRequest) => {
    try {
      setIsLoading(true);
      const response = await authService.verifyTwoFactor(code);
      if (response.success && response.data?.user) {
        setUser(response.data.user);
        
        // Initialize permissions after successful 2FA
        try {
          await multiTenantPermissionService.initializeUserPermissions();
        } catch (permError) {
          console.warn('⚠️ Erreur lors de l\'initialisation des permissions:', permError);
        }
      } else {
        throw new Error(response.message || '2FA failed');
      }
    } catch (error) {
      console.error('2FA failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = useCallback(async () => {
    try {
      setIsLoading(true);
      setUser(null);
      await sessionsService.deactivateCurrentSession();
      await authService.logout();
      sessionsService.clearClientSessionKey();
      setUser(null);
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      } else {
        window.location.reload();
      }
    } catch (error) {
      console.error('Logout failed:', error);
      sessionsService.stopActivityUpdates();
      sessionsService.clearClientSessionKey();
      setUser(null);
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  const changePassword = async (data: { current_password: string; new_password: string; confirm_password: string }) => {
    try {
      await authService.changePassword(data);
    } catch (error) {
      console.error('Change password failed:', error);
      throw error;
    }
  };

  const forgotPassword = async (email: string) => {
    try {
      await authService.forgotPassword(email);
    } catch (error) {
      console.error('Forgot password failed:', error);
      throw error;
    }
  };

  const refreshUser = async () => {
    try {
      const userData = await authService.getUserProfile();
      
      // Convert UserProfile to User type
      const user: User = {
        id: userData.id,
        username: userData.username,
        email: userData.email,
        first_name: userData.first_name,
        last_name: userData.last_name,
        is_active: true, // Default value
        is_staff: false, // Default value
        is_superuser: false, // Default value
        date_joined: new Date().toISOString(), // Default value
        last_login: new Date().toISOString(), // Default value
      };
      setUser(user);
      
      // Initialize permissions after user is set
      try {
        await multiTenantPermissionService.initializeUserPermissions();
      } catch (permError) {
        console.warn('⚠️ Erreur lors de l\'initialisation des permissions:', permError);
      }
    } catch (error) {
      console.error('❌ Échec du rafraîchissement de l\'utilisateur:', error);
      setUser(null);
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    twoFactorAuth,
    changePassword,
    forgotPassword,
    logout,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
